import { CloseIcon, DragHandleIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  ListItem,
  Text,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { BranchIcon } from 'components/atoms/Icons';
import { CSSProperties, FC } from 'react';
import { Crawler } from 'types/crawler';
import { CrawlerAddButton } from '../CrawlerAddButton/CrawlerAddButton';

type Props = {
  id: number;
  name: string;
  parentId: number;
  crawlers: Crawler[];
  update: boolean;
  canAddChild: boolean;
  handleClickAdd: (id: number, parentId: number) => void;
  handleDelete: (id: number, parentId: number) => void;
};

export const CrawlersListItem: FC<Props> = ({
  id,
  name,
  parentId,
  crawlers,
  update,
  canAddChild,
  handleClickAdd,
  handleDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    position: 'relative',
  };

  const [hover, setHover] = useBoolean();
  const isHover = hover && !update;

  return (
    <ListItem
      key={id}
      ref={setNodeRef}
      style={style}
      onMouseEnter={setHover.on}
      onMouseLeave={setHover.off}
    >
      <Flex gap="8px" align="center">
        <CrawlerAddButton
          id={id}
          parentId={parentId}
          crawlers={crawlers}
          visible={isHover}
          toChild={false}
          onClick={handleClickAdd}
        />
        {canAddChild && (
          <CrawlerAddButton
            id={id}
            parentId={parentId}
            crawlers={crawlers}
            visible={isHover}
            toChild
            onClick={handleClickAdd}
          />
        )}
        <Tooltip label="クローラーを並び替える">
          <IconButton
            size="xs"
            aria-label="クローラー並び替え"
            icon={<DragHandleIcon />}
            {...listeners}
            {...attributes}
            cursor={isDragging ? 'grabbing' : 'grab'}
            visibility={isHover ? 'visible' : 'hidden'}
          />
        </Tooltip>
        {parentId === -1 && <Box w="8px" h="8px" bg="gray.500" />}
        {parentId !== -1 && <BranchIcon boxSize="4" color="gray.500" />}
        <Box flexGrow={1} display="flex" alignItems="center" gap="16px">
          <Text fontSize="sm" color="gray.500">
            {id}
          </Text>
          <Text fontSize="md" maxW="400px" noOfLines={1}>
            {name}
          </Text>
        </Box>
        <Tooltip label="クローラーを削除する">
          <IconButton
            size="xs"
            aria-label="クローラー削除"
            icon={<CloseIcon />}
            onClick={() => handleDelete(id, parentId)}
            visibility={isHover ? 'visible' : 'hidden'}
          />
        </Tooltip>
      </Flex>
    </ListItem>
  );
};
